import React from "react";
import { Shield, FileText, Users, AlertTriangle, ExternalLink, Heart, Play, Mail, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const EXTERNAL_SITE = "https://exposed.fightfitash.gov.jonesaica.yahsharal.info/exposed";
const YOUTUBE_URL = "https://www.youtube.com/@TheWomanInGold77";
const TIKTOK_URL = "https://www.tiktok.com/@theqadashsheep77";
const ETSY_URL = "https://www.etsy.com/shop/AncientPathFactory";

export default function BrandFightFiTash() {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="brand-fightfitash-page">
      {/* Hero */}
      <section className="bg-scarlett text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <span className="text-kingdom-gold text-sm font-bold uppercase tracking-wider">
            #FightFiTash Campaign
          </span>
          <h1 className="heading-hero mt-4 mb-6 text-white">
            STATE-SANCTIONED CHILD ABUSE COVER-UP
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mb-8">
            Federal judges acknowledged abuse occurred in State Custody. They still hide under abstention.
            This campaign exposes every judge, clerk, and oath-breaker involved in trafficking our offspring.
          </p>
          
          {/* Qadash Sheep Socials - Prominent */}
          <div className="flex flex-wrap gap-4 mb-8">
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded transition-colors">
              <Play className="w-5 h-5 text-kingdom-gold" />
              <span>YouTube</span>
            </a>
            <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded transition-colors">
              <Play className="w-5 h-5 text-kingdom-gold" />
              <span>TikTok</span>
            </a>
          </div>
          
          {/* Video Placeholder */}
          <div className="bg-black/50 border-2 border-dashed border-kingdom-gold rounded-lg p-12 max-w-2xl mb-8">
            <div className="text-center">
              <Play className="w-16 h-16 text-kingdom-gold mx-auto mb-4" />
              <p className="text-kingdom-gold font-semibold">Opening Video</p>
              <p className="text-slate-400 text-sm mt-2">
                Video placeholder - will be added via GitHub after deployment
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer">
              <Button className="bg-kingdom-gold hover:bg-kingdom-gold/90 text-black font-bold">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Full FightFiTash Site
              </Button>
            </a>
            <a href="https://cash.app/$JonesaicaYahsharal" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                <Heart className="w-4 h-4 mr-2" />
                Donate to the Fight
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Navigation Anchors */}
      <section className="bg-slate-900 py-4 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => scrollToSection("evidence-vault")} className="text-white hover:text-kingdom-gold transition-colors px-3 py-1">Evidence Vault</button>
            <button onClick={() => scrollToSection("oath-breakers")} className="text-white hover:text-kingdom-gold transition-colors px-3 py-1">Oath Breakers Wall</button>
            <button onClick={() => scrollToSection("get-involved")} className="text-white hover:text-kingdom-gold transition-colors px-3 py-1">Get Involved</button>
            <button onClick={() => scrollToSection("documents")} className="text-white hover:text-kingdom-gold transition-colors px-3 py-1">Documents</button>
            <button onClick={() => scrollToSection("merch-support")} className="text-white hover:text-kingdom-gold transition-colors px-3 py-1">Merch / Support</button>
          </div>
        </div>
      </section>

      {/* Evidence Vault Section */}
      <section id="evidence-vault" className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="w-10 h-10 text-scarlett" />
            <h2 className="font-heading text-3xl text-white">Evidence Vault</h2>
          </div>
          <p className="text-slate-400 mb-8 max-w-3xl">
            Court documents, unsealed orders, and federal filings proving the abuse and cover-up.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <FileText className="w-8 h-8 text-kingdom-gold mb-4" />
                <h3 className="text-white font-semibold mb-2">Court Filings</h3>
                <p className="text-slate-400 text-sm mb-4">Federal court documents and appeals</p>
                <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="text-kingdom-gold text-sm hover:underline">View Documents →</a>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <Shield className="w-8 h-8 text-kingdom-gold mb-4" />
                <h3 className="text-white font-semibold mb-2">Unsealed Orders</h3>
                <p className="text-slate-400 text-sm mb-4">Orders exposing judicial misconduct</p>
                <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="text-kingdom-gold text-sm hover:underline">View Orders →</a>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <AlertTriangle className="w-8 h-8 text-kingdom-gold mb-4" />
                <h3 className="text-white font-semibold mb-2">Timeline of Abuse</h3>
                <p className="text-slate-400 text-sm mb-4">Documented history of violations</p>
                <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="text-kingdom-gold text-sm hover:underline">View Timeline →</a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Oath Breakers Wall Section */}
      <section id="oath-breakers" className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <AlertTriangle className="w-10 h-10 text-scarlett" />
            <h2 className="font-heading text-3xl text-white">Oath Breakers Wall</h2>
          </div>
          <p className="text-slate-400 mb-8 max-w-3xl">
            Every judge, clerk, caseworker, and official who violated their oath. Named and documented.
          </p>
          <div className="bg-scarlett/10 border border-scarlett/30 rounded-lg p-8 text-center">
            <p className="text-white mb-4">View the complete list of oath breakers at the full site</p>
            <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer">
              <Button className="bg-scarlett hover:bg-scarlett/90 text-white">
                View Oath Breakers Wall
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section id="get-involved" className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Users className="w-10 h-10 text-royal-purple" />
            <h2 className="font-heading text-3xl text-white">Get Involved</h2>
          </div>
          <p className="text-slate-400 mb-8 max-w-3xl">
            Volunteer, donate, write letters, spread the word. This is a movement, not a moment.
          </p>
          <div className="grid sm:grid-cols-3 gap-6">
            <a href="https://cash.app/$JonesaicaYahsharal" target="_blank" rel="noopener noreferrer" className="p-6 bg-kingdom-gold rounded-lg hover:bg-kingdom-gold/90 transition-colors text-center">
              <Heart className="w-8 h-8 text-black mx-auto mb-2" />
              <span className="font-bold text-black">Donate</span>
            </a>
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSe5V5QxF922a3XYIZCGveUf-THGQ83ZqislyeUQil2fKtwugA/viewform" target="_blank" rel="noopener noreferrer" className="p-6 bg-royal-purple rounded-lg hover:bg-royal-purple/90 transition-colors text-center">
              <Users className="w-8 h-8 text-white mx-auto mb-2" />
              <span className="font-bold text-white">Volunteer</span>
            </a>
            <a href="https://docs.google.com/document/d/1WfOmVsZl5PkTxavc62pGqz4qUMsl506FT5FZhY75koM/edit" target="_blank" rel="noopener noreferrer" className="p-6 bg-royal-blue rounded-lg hover:bg-royal-blue/90 transition-colors text-center">
              <Mail className="w-8 h-8 text-white mx-auto mb-2" />
              <span className="font-bold text-white">Write Letters</span>
            </a>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-10 h-10 text-royal-blue" />
            <h2 className="font-heading text-3xl text-white">Documents & Filings</h2>
          </div>
          <p className="text-slate-400 mb-8 max-w-3xl">
            Supreme Court petitions, Third Circuit appeals, and all legal filings in the case.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-950 border-slate-800">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4">Legal Filings</h3>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li>• SCOTUS Petition for Certiorari</li>
                  <li>• Third Circuit Appeal Documents</li>
                  <li>• Federal District Court Filings</li>
                  <li>• Habeas Corpus Petitions</li>
                </ul>
                <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                  <Button variant="outline" className="border-kingdom-gold text-kingdom-gold hover:bg-kingdom-gold/10">
                    View All Filings
                  </Button>
                </a>
              </CardContent>
            </Card>
            <Card className="bg-slate-950 border-slate-800">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4">Evidence Documents</h3>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li>• Medical Records Proving Abuse</li>
                  <li>• Social Worker Misconduct Reports</li>
                  <li>• Communication Records</li>
                  <li>• Witness Statements</li>
                </ul>
                <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                  <Button variant="outline" className="border-kingdom-gold text-kingdom-gold hover:bg-kingdom-gold/10">
                    View Evidence
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Merch / Support Section */}
      <section id="merch-support" className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <ShoppingBag className="w-10 h-10 text-kingdom-gold" />
            <h2 className="font-heading text-3xl text-white">Merch / Support</h2>
          </div>
          <p className="text-slate-400 mb-8 max-w-3xl">
            Support the Fight Fi Tash campaign with official merchandise. All proceeds go to legal fees and advocacy.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Official Merch</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 mb-4">T-shirts, hoodies, and more available through our Etsy shop.</p>
                <a href={ETSY_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="btn-gold w-full">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Shop on Etsy
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Direct Donation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 mb-4">Every dollar goes directly to fighting for Tash's freedom.</p>
                <a href="https://cash.app/$JonesaicaYahsharal" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-royal-purple hover:bg-royal-purple/90 text-white w-full">
                    <Heart className="w-4 h-4 mr-2" />
                    Donate via CashApp
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-scarlett py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/90 text-lg italic">
            "Free by blood. Protected by YaHUaH. No contract, no consent, no court fi Babylon."
          </p>
          <a href={EXTERNAL_SITE} target="_blank" rel="noopener noreferrer" className="inline-block mt-6">
            <Button className="bg-white text-scarlett hover:bg-slate-100 font-bold">
              Visit yahsharal.info/fightfitash
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
